import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { AlertCircle } from 'lucide-react';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';

interface NumericFieldProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  allowDecimals?: boolean;
  /** Format with thousand separators (e.g., 1,234) */
  showCommas?: boolean;
  /** Add $ prefix and format as currency */
  formatAsCurrency?: boolean;
  /** Add % suffix */
  formatAsPercent?: boolean;
  /** Error message to display */
  error?: string;
  /** Whether to show validation state visually */
  showValidation?: boolean;
  /** Callback when validation error occurs */
  onValidationError?: (error: string | null) => void;
}

/**
 * Format a number with thousand separators
 */
const formatWithCommas = (value: number, decimals: boolean = true): string => {
  if (decimals) {
    return value.toLocaleString('en-US');
  }
  return Math.round(value).toLocaleString('en-US');
};

/**
 * Parse a formatted string back to number (removes commas, $, %)
 */
const parseFormattedNumber = (value: string): number => {
  const cleaned = value.replace(/[^0-9.-]/g, '');
  return Number(cleaned) || 0;
};

/**
 * A numeric input field that allows proper backspace deletion by using local string state.
 * Supports comma formatting, currency/percent display, validation feedback, and haptic feedback.
 */
export const NumericField: React.FC<NumericFieldProps> = ({
  value,
  onChange,
  min,
  max,
  step,
  allowDecimals = true,
  showCommas = false,
  formatAsCurrency = false,
  formatAsPercent = false,
  error,
  showValidation = false,
  onValidationError,
  className,
  ...props
}) => {
  const [localValue, setLocalValue] = useState<string>('');
  const [isFocused, setIsFocused] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const { triggerHaptic, isSupported: hasHaptics } = useHapticFeedback();
  const prevErrorRef = useRef<string | null>(null);

  // Format the display value based on options
  const formatDisplayValue = useCallback((num: number): string => {
    if (num === 0) return '';
    
    let formatted: string;
    if (showCommas || formatAsCurrency) {
      formatted = formatWithCommas(num, allowDecimals);
    } else {
      formatted = String(num);
    }
    
    return formatted;
  }, [showCommas, formatAsCurrency, allowDecimals]);

  // Sync from external value only when not focused
  useEffect(() => {
    if (!isFocused) {
      setLocalValue(formatDisplayValue(value));
    }
  }, [value, isFocused, formatDisplayValue]);

  // Validate value against min/max
  const validateValue = useCallback((num: number): string | null => {
    if (min !== undefined && num < min) {
      return `Minimum value is ${formatAsCurrency ? '$' : ''}${min}${formatAsPercent ? '%' : ''}`;
    }
    if (max !== undefined && num > max) {
      return `Maximum value is ${formatAsCurrency ? '$' : ''}${max}${formatAsPercent ? '%' : ''}`;
    }
    if (!allowDecimals && !Number.isInteger(num) && num !== 0) {
      return 'Decimals not allowed';
    }
    return null;
  }, [min, max, allowDecimals, formatAsCurrency, formatAsPercent]);

  // Trigger haptic feedback when validation error changes
  useEffect(() => {
    const currentError = error || (showValidation ? validationError : null);
    if (currentError && currentError !== prevErrorRef.current && hasHaptics) {
      triggerHaptic('error');
    }
    prevErrorRef.current = currentError;
    onValidationError?.(currentError);
  }, [error, validationError, showValidation, hasHaptics, triggerHaptic, onValidationError]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    // Allow empty string, minus sign, or decimal point while typing
    if (inputValue === '' || inputValue === '-' || inputValue === '.') {
      setLocalValue(inputValue);
      setValidationError(null);
      return;
    }

    // Remove formatting characters for validation (commas, $, %)
    const cleanedForParsing = inputValue.replace(/[,$%\s]/g, '');
    
    // Validate the input format
    const regex = allowDecimals ? /^-?\d*\.?\d*$/ : /^-?\d*$/;
    if (!regex.test(cleanedForParsing)) {
      return;
    }

    setLocalValue(inputValue);

    // Parse and propagate valid numbers immediately
    const numericValue = parseFloat(cleanedForParsing);
    if (!isNaN(numericValue)) {
      // Validate on change for immediate feedback
      const error = validateValue(numericValue);
      setValidationError(error);
      onChange(numericValue);
    }
  }, [allowDecimals, onChange, validateValue]);

  const handleFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    // Show raw value without formatting when focused for easier editing
    if (value !== 0) {
      setLocalValue(String(value));
    }
    // Select all text after a short delay to ensure state is updated
    setTimeout(() => {
      e.target.select();
    }, 0);
  }, [value]);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    
    // On blur, finalize the value
    if (localValue === '' || localValue === '-' || localValue === '.') {
      onChange(0);
      setLocalValue('');
      setValidationError(null);
      return;
    }

    let numericValue = parseFormattedNumber(localValue);
    if (isNaN(numericValue)) {
      numericValue = 0;
    }

    // Apply min/max constraints on blur
    if (min !== undefined && numericValue < min) {
      numericValue = min;
    }
    if (max !== undefined && numericValue > max) {
      numericValue = max;
    }

    onChange(numericValue);
    setLocalValue(formatDisplayValue(numericValue));
    setValidationError(validateValue(numericValue));
  }, [localValue, onChange, min, max, formatDisplayValue, validateValue]);

  // Determine if there's an error to show
  const displayError = error || (showValidation ? validationError : null);
  const hasError = !!displayError;

  return (
    <div className="relative w-full">
      {/* Currency prefix - only show when formatAsCurrency and not focused */}
      {formatAsCurrency && !isFocused && localValue && (
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none z-10">
          $
        </span>
      )}
      
      <Input
        type="text"
        inputMode="decimal"
        value={localValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className={cn(
          formatAsCurrency && !isFocused && localValue && "pl-7",
          formatAsPercent && "pr-7",
          hasError && "border-destructive focus-visible:ring-destructive",
          className
        )}
        {...props}
      />
      
      {/* Percent suffix */}
      {formatAsPercent && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
          %
        </span>
      )}
      
      {/* Error icon */}
      {hasError && (
        <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-destructive" />
      )}
      
      {/* Error message */}
      {displayError && (
        <p className="text-xs text-destructive mt-1 flex items-center gap-1">
          {displayError}
        </p>
      )}
    </div>
  );
};

export default NumericField;
